import { useCallback, useEffect, useRef, useState } from "react";
import type { ReadInfusionSurface } from "@ferrivia/api-contract";
import { useApi } from "@ferrivia/auth-web";

export type InfusionSurfaceState =
  | { status: "loading" }
  | { status: "error"; error: string }
  | { status: "success"; surface: ReadInfusionSurface };

export interface UseInfusionSurfaceResult {
  state: InfusionSurfaceState;
  // Re-runs the fetch without resetting to "loading", so a finished step
  // updates in place. Same shape as useHomeSurface.
  refetch: () => void;
}

export function useInfusionSurface(): UseInfusionSurfaceResult {
  const api = useApi();
  const [state, setState] = useState<InfusionSurfaceState>({ status: "loading" });
  const cancelledRef = useRef(false);

  const fetchSurface = useCallback(() => {
    api.infusion
      .get()
      .then((surface) => {
        if (!cancelledRef.current) {
          setState({ status: "success", surface });
        }
      })
      .catch((err: unknown) => {
        if (!cancelledRef.current) {
          setState({ status: "error", error: err instanceof Error ? err.message : "Failed to load infusion steps" });
        }
      });
  }, [api]);

  useEffect(() => {
    cancelledRef.current = false;
    fetchSurface();
    return () => {
      cancelledRef.current = true;
    };
  }, [fetchSurface]);

  return { state, refetch: fetchSurface };
}
